/**
 * StoryCard — one of 3 narrative tiles below the HeadlineHero.
 *
 * V2.6 Worker-B (2026-04-20). Each card answers a single question about
 * the event ("biggest day", "top attraction", "notable anomaly") with a
 * headline value + a one-line supporting fact. Not a metric tile — a
 * tiny story.
 *
 * Supports two tones:
 *   - `default`: bg-white, neutral hairline. Positive / factual stories.
 *   - `watch`:   bg-surface-stripe + glow-300 left accent. Use for the
 *                anomaly / "needs attention" card so the eye catches it.
 *
 * Caller passes pre-formatted strings so the card stays dumb.
 */

export type StoryCardTone = 'default' | 'watch' | 'empty'

export interface StoryCardProps {
  /** Short upper-case heading ("Biggest Day", "Top Ride", "Watch"). */
  title: string
  /** Big display line ("Sun Apr 19", "THE KING", "Fri Apr 17"). */
  primaryValue: string
  /** Short label beneath the value ("$208,000", "$39,907 · 5,701 scans"). */
  primaryLabel?: string
  /** One-line supporting fact, full sentence fragment. */
  supporting?: string
  tone?: StoryCardTone
  'data-testid'?: string
}

export function StoryCard({
  title,
  primaryValue,
  primaryLabel,
  supporting,
  tone = 'default',
  'data-testid': testId,
}: StoryCardProps) {
  const resolvedTestId =
    testId ?? `story-card-${title.toLowerCase().replace(/\s+/g, '-')}`

  if (tone === 'empty') {
    return (
      <div
        data-testid={resolvedTestId}
        data-tone="empty"
        className="rounded-card border border-dashed border-hairline bg-surface-stripe p-4 shadow-card-soft dark:border-slate-700 dark:bg-slate-800/60"
      >
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-ink-soft dark:text-slate-400">
          {title}
        </div>
        <div className="mt-2 font-display text-xl font-semibold text-ink-soft dark:text-slate-400">
          {primaryValue || '—'}
        </div>
        {supporting ? (
          <div className="mt-2 text-xs text-ink-soft dark:text-slate-400">
            {supporting}
          </div>
        ) : null}
      </div>
    )
  }

  const toneSkin =
    tone === 'watch'
      ? 'border-glow-300 bg-surface-stripe dark:border-glow-700 dark:bg-slate-800'
      : 'border-hairline bg-white dark:border-slate-700 dark:bg-slate-800'
  const titleSkin =
    tone === 'watch'
      ? 'text-marquee-700 dark:text-marquee-200'
      : 'text-butler-600 dark:text-butler-200'

  return (
    <article
      data-testid={resolvedTestId}
      data-tone={tone}
      className={`group relative overflow-hidden rounded-card border p-4 shadow-card-soft transition hover:shadow-card hover:-translate-y-0.5 ${toneSkin}`}
    >
      {tone === 'watch' ? (
        <div
          aria-hidden="true"
          className="absolute inset-y-0 left-0 w-[3px] bg-marquee-500"
        />
      ) : null}
      <div
        className={`text-[11px] font-semibold uppercase tracking-[0.16em] ${titleSkin}`}
      >
        {title}
      </div>
      <div
        data-testid={`${resolvedTestId}-value`}
        className="mt-2 font-display text-2xl font-bold tracking-tight text-butler-800 dark:text-butler-50"
      >
        {primaryValue}
      </div>
      {primaryLabel ? (
        <div
          data-testid={`${resolvedTestId}-primary-label`}
          className="mt-1 font-display text-sm font-semibold text-butler-700 tabular-nums dark:text-butler-100"
        >
          {primaryLabel}
        </div>
      ) : null}
      {supporting ? (
        <div
          data-testid={`${resolvedTestId}-supporting`}
          className="mt-2 text-xs text-ink-soft dark:text-slate-400"
        >
          {supporting}
        </div>
      ) : null}
    </article>
  )
}
